import React from 'react';
import { failureModeCreateSchema } from '../../../../../common/src/domain/survey/failureMode/failureModeSchema';
import { ITranslateProps, translate } from '../../../mixins/translate';
import { fetchGet } from '../../../store/fetch';
import {
    ramsAOptions,
    ramsCOptions,
    ramsEcOptions,
    ramsEnvOptions,
    ramsPOptions,
    ramsROptions,
    ramsSOptions,
    getTotalPriorityValue,
    getWeightedPriorityValue,
} from '../../../utils/data/rams';
import { EditPanel, EditPanelButtons, EditPanelHeader, EditPanelRow } from '../../elements';
import { Component } from '../../utils/Component';
import { ValidatingEditPanelSaveButton, ValidatingForm, ValidatingFormTextArea } from '../../validatingElements';
import { IAsset } from '../Interfaces/IAsset';
import { RamssheepDropdownFmeca } from './RamsDropdownFMECA';
import { TotalRiskScore } from './TotalRiskScore';

interface IMaintenanceFailureModeEditProps extends ITranslateProps {
    objectId: string;
    surveyId: string;
    elementId?: undefined | string;
    unitId?: undefined | string;
    manifestationId?: undefined | string;
    onCancel: () => void;
    onSaved: () => Promise<void>;
    failureModeId?: undefined | string;
    onRemove: (id: string) => Promise<void>;
}

interface IFinding {
    id: string;
    description: string;
}
interface IMaintenanceMeasure {
    id: string;
    description: string;
    cost?: undefined | number;
    planYear?: undefined | number;
}

interface IMaintenanceFailureModeEditState {
    assets: IAsset[];
    findings: IFinding[];
    maintenanceMeasures: IMaintenanceMeasure[];
    isUploading: boolean;
    selectedMaintenanceMeasureId?: undefined | string;
    showMaintenanceMeasureEdit: boolean;
}

class MaintenanceFailureModeEditComponent extends Component<IMaintenanceFailureModeEditProps, IMaintenanceFailureModeEditState> {
    constructor(props: IMaintenanceFailureModeEditProps) {
        super(props);

        this.state = {
            assets: [],
            findings: [],
            maintenanceMeasures: [],
            isUploading: false,
            showMaintenanceMeasureEdit: false,
        };
    }

    public override async componentDidMount() {
        await this.init();
    }

    public override async componentDidUpdate(prevProps: IMaintenanceFailureModeEditProps) {
        if (prevProps.failureModeId !== this.props.failureModeId) {
            await this.init();
        }
    }

    public override render() {
        return (
            <>
                <EditPanel>
                    <ValidatingForm
                        schema={failureModeCreateSchema}
                        createUrl={`/api/surveys/${this.props.surveyId}/failureModes/`}
                        createUrlQueryParameters={{
                            elementId: this.props.elementId,
                            unitId: this.props.unitId,
                            manifestationId: this.props.manifestationId,
                        }}
                        onSubmitted={async () => this.props.onSaved()}
                        itemUrl={`/api/surveys/${this.props.surveyId}/failureModes/${this.props.failureModeId}`}
                        id={this.props.failureModeId}
                        onChange={async (key, _value, item, onChange) => this.handleItemChanged(key, item, onChange)}
                    >
                        <EditPanelRow>
                            <EditPanelHeader text={this.props.t('failureMode.maintenanceRiskScoreHeader')} />
                        </EditPanelRow>
                        <EditPanelRow>
                            <div className="col-md-8">
                                <RamssheepDropdownFmeca
                                    name="maintenanceRamsR"
                                    topLabel="R"
                                    header="Betrouwbaarheid"
                                    subText="Manifestatie van risico"
                                    options={ramsROptions}
                                />
                                <RamssheepDropdownFmeca
                                    name="maintenanceRamsA"
                                    topLabel="A"
                                    header="Beschikbaarheid"
                                    subText="Fractie van tijd dat object niet functioneert"
                                    options={ramsAOptions}
                                />
                                <RamssheepDropdownFmeca
                                    name="maintenanceRamsS"
                                    topLabel="S"
                                    header="Veiligheid"
                                    subText="ARBO- en verkeersveiligheid"
                                    options={ramsSOptions}
                                />
                                <RamssheepDropdownFmeca
                                    name="maintenanceRamsC"
                                    topLabel="C"
                                    header="Verzorgingsniveau"
                                    subText="Beeldkwalitieit van het object"
                                    options={ramsCOptions}
                                />
                                <RamssheepDropdownFmeca
                                    name="maintenanceRamsEc"
                                    topLabel="E"
                                    header="Gevolgkosten"
                                    subText="De totale corectieve kosten"
                                    options={ramsEcOptions}
                                />
                                <RamssheepDropdownFmeca
                                    name="maintenanceRamsEnv"
                                    topLabel="E"
                                    header="Milieu"
                                    subText="Negatief effect op leefomgevin"
                                    options={ramsEnvOptions}
                                />
                                <RamssheepDropdownFmeca
                                    name="maintenanceRamsP"
                                    topLabel="P"
                                    header="Imago"
                                    subText="Media en politieke aandacht"
                                    options={ramsPOptions}
                                />
                            </div>
                            <div className="col-md-4 mt35">
                                <TotalRiskScore step="maintenance" />
                            </div>
                        </EditPanelRow>

                        {/* Remark */}
                        {/* <EditPanelRow>
                            <EditPanelHeader text={this.props.t('common.remarks')} />
                        </EditPanelRow> */}
                        <EditPanelRow>
                            <div className="col-md-12">
                                <ValidatingFormTextArea name="maintenanceRemarks" saveOnBlur={true} placeholder={this.props.t('common.remarks')} />
                            </div>
                        </EditPanelRow>

                        <EditPanelButtons>
                            <ValidatingEditPanelSaveButton small={true} />
                        </EditPanelButtons>
                    </ValidatingForm>
                </EditPanel>
            </>
        );
    }

    private async handleItemChanged(key: string, item: any, onChange: (key: any, value: any) => Promise<void>) {
        const keysToWatch = [
            'maintenanceRamsR',
            'maintenanceRamsA',
            'maintenanceRamsS',
            'maintenanceRamsC',
            'maintenanceRamsEc',
            'maintenanceRamsEnv',
            'maintenanceRamsP',
        ];

        if (keysToWatch.includes(key)) {
            const maintenanceRamsTotalPriority = getTotalPriorityValue(
                item.maintenanceRamsR,
                item.maintenanceRamsA,
                item.maintenanceRamsS,
                item.maintenanceRamsC,
                item.maintenanceRamsEc,
                item.maintenanceRamsEnv,
                item.maintenanceRamsP,
            );
            const maintenanceRamsWeightedPriority = getWeightedPriorityValue(
                item.maintenanceRamsR,
                item.maintenanceRamsA,
                item.maintenanceRamsS,
                item.maintenanceRamsC,
                item.maintenancenRamsEc,
                item.maintenanceRamsEnv,
                item.maintenanceRamsP,
            );
            await onChange('maintenanceRamsTotalPriority', maintenanceRamsTotalPriority.toString());
            await onChange('maintenanceRamsWeightedPriority', maintenanceRamsWeightedPriority);
        }
    }

    private async init() {
        // await this.loadAssets();
        await this.loadFindings();
        await this.loadMaintenanceMeasures();
    }

    private async loadMaintenanceMeasures() {
        const result = await fetchGet(`/api/surveys/${this.props.surveyId}/maintenanceMeasures?failureModeId=${this.props.failureModeId}`);
        await this.setState({ maintenanceMeasures: result.json });
    }

    private async loadFindings() {
        const result = await fetchGet(`/api/surveys/${this.props.surveyId}/failureModes/${this.props.failureModeId}/findings`);
        await this.setState({ findings: result.json });
    }
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export const MaintenanceFailureModeEdit = translate()(MaintenanceFailureModeEditComponent);
